// Third Party
import 'react-dates/initialize';
import * as React from 'react';
import {
  DayPickerRangeController,
  DayPickerRangeControllerShape,
  FocusedInputShape,
} from 'react-dates';
import moment from 'moment';

// Utils
import { FeatureKey } from '@gql';
import { formatDateYYYYMMDD, formatDateMMMD } from '@utils/date-helpers';
import { formatWholeDollarAmount } from '@utils/money';
import { guestCapacity } from '@utils/constants/guest-capacity-thresholds';
import { ListingBySlugPartial } from '../utils';
import { useQuotes } from '../quotes-context';

// UI
import { AnalyticsRef } from '@components/analytics-ref';
import { Button } from '@components/button';
import { ButtonIcon } from '@components/button-icon';
import { Dropdown } from '@components/dropdown';
import { IconNames } from '@components/icon';
import { Input } from '@components/form/input';
import { Link } from '@components/link';
import { ListingQuoteBreakdown } from '@components/listing-quote-breakdown';
import { Spinner } from '@components/spinner';
import { Typography } from '@components/typography';
import { QuotesCompare } from '../quotes-compare';

export type Props = {
  cta: {
    label: string;
    subtext?: string;
    bookingUrl?: string;
    analyticsRef: string;
  };
  listing: ListingBySlugPartial;
  nightlyPriceInfo: string | JSX.Element;
  shouldShowRatesAndAvailability: boolean;
};

interface IHandleDatesChange {
  startDate: moment.Moment | null;
  endDate: moment.Moment | null;
}

// Cast DayPickerRangeController to ComponentType to resolve React JSX type conflicts
// This tells TypeScript to treat it as a valid React component that can accept props
const DayPickerComponent =
  DayPickerRangeController as React.ComponentType<DayPickerRangeControllerShape>;

export const QuotesCta: React.FC<Props> = ({
  cta,
  listing,
  nightlyPriceInfo,
  shouldShowRatesAndAvailability,
}) => {
  const dateRangePickerInputRef = React.useRef<HTMLDivElement>(null);
  const [dateRangePickerFocusedInput, setDateRangePickerFocusedInput] =
    React.useState<FocusedInputShape | null>(null);

  // This is cached so that it does not cause a re-render
  // in the date range picker component
  const currentMoment = React.useMemo(moment, []);

  const {
    availabilityDataLoading,
    quoteData,
    adultCount,
    setAdultCount,
    childCount,
    setChildCount,
    infantCount,
    setInfantCount,
    petCount,
    guestCountHumanReadableValue,
    guestCapacityReached,
    setPetCount,
    checkInDate,
    setCheckInDate,
    checkOutDate,
    setCheckOutDate,
    isDayBlocked,
    isStartDayBlocked,
    isEndDayBlocked,
    minimumNightsForSelectedDate,
    getMinNightsForHoverDate,
    datesPresent,
    datesInvalid,
    numberOfNights,
    quoteDataLoading,
  } = useQuotes();

  const petsNotAllowed = listing.features.some(
    (feature) => feature.key === FeatureKey.PetsNotAllowed
  );

  const guestCountInputRef = React.useRef<HTMLDivElement>(null);
  const [guestCountFocusedInput, setGuestCountFocusedInput] =
    React.useState(false);

  const onDatesChange = React.useCallback(
    ({ startDate, endDate }: IHandleDatesChange) => {
      setCheckInDate(startDate ? formatDateYYYYMMDD(startDate) : null);
      setCheckOutDate(endDate ? formatDateYYYYMMDD(endDate) : null);
    },
    []
  );

  const onDropdownClickAway = React.useCallback(() => {
    setDateRangePickerFocusedInput(null);
  }, []);

  const onClearDatesClick = React.useCallback(() => {
    setCheckInDate(null);
    setCheckOutDate(null);
    setDateRangePickerFocusedInput('startDate');
  }, []);

  const handleValueChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setValue: React.Dispatch<React.SetStateAction<number>>,
    maxValue: guestCapacity
  ) => {
    e.preventDefault();
    const re = /^[0-9\b]+$/;
    if (e.target.value.startsWith('0')) {
      e.target.value = e.target.value.slice(1);
    }
    if (e.target.value === '' || re.test(e.target.value)) {
      const inputValue = Number(e.target.value);
      const value = inputValue > maxValue ? maxValue : inputValue;
      setValue(value);
    }
  };

  return (
    <div className="w-full">
      {nightlyPriceInfo !== '' ? (
        <div className="ml-1 flex w-full items-start space-x-1 h-6">
          <Typography
            component="span"
            variant="body2"
            variantL="body1"
            className="font-semibold lg:font-semibold flex items-center whitespace-nowrap text-primary-main"
          >
            {nightlyPriceInfo}
          </Typography>
          {datesPresent && (
            <Typography
              component="span"
              variant="body2"
              variantL="body1"
              className="flex font-medium lg:font-medium items-center whitespace-nowrap text-primary-main"
            >
              night
            </Typography>
          )}
        </div>
      ) : quoteDataLoading ? (
        <div className="h-6">
          <Spinner />
        </div>
      ) : null}
      <div className="w-full flex flex-col mt-3 whitespace-nowrap border-primary-main border-2 rounded-lg bg-white">
        <div className="w-full flex lg:space-x-2 whitespace-nowrap">
          <div className="min-w-[90px] py-1 border-primary-main border-r-2">
            <Input
              hasBorder={false}
              label="Check-in"
              name="checkInDate"
              value={
                checkInDate ? formatDateMMMD(moment(checkInDate)) : undefined
              }
              onFocus={() => {
                setDateRangePickerFocusedInput('startDate');
              }}
              isInvalid={datesInvalid || false}
              autoComplete="off"
              hasCaret={false}
            />
          </div>
          <div className="min-w-[90px] py-1">
            <Input
              hasBorder={false}
              label="Check-out"
              name="checkOutDate"
              value={
                checkOutDate ? formatDateMMMD(moment(checkOutDate)) : undefined
              }
              onFocus={() => {
                if (checkOutDate) {
                  setDateRangePickerFocusedInput('endDate');
                } else {
                  setDateRangePickerFocusedInput('startDate');
                }
              }}
              isInvalid={datesInvalid || false}
              autoComplete="off"
              hasCaret={false}
            />
          </div>
        </div>
        <div className="min-w-[120px] w-full py-1 border-primary-main border-t-2">
          <Input
            hasBorder={false}
            style={{ caretColor: 'transparent' }}
            label="Guests"
            name="guests"
            value={guestCountHumanReadableValue}
            onFocus={() => {
              setGuestCountFocusedInput(true);
            }}
            autoComplete="off"
          />
        </div>
      </div>

      <Dropdown
        dropdownWidth={'full'}
        reference={dateRangePickerInputRef.current}
        isOpen={!!dateRangePickerFocusedInput}
        onClickAway={onDropdownClickAway}
        data-id="date-range-picker-dropdown"
        className="right-0 lg:right-auto absolute -mt-[59px]"
      >
        <div className="flex justify-start items-start w-full">
          <DayPickerComponent
            verticalBorderSpacing={0}
            enableOutsideDays={false}
            minDate={currentMoment}
            noBorder={true}
            numberOfMonths={1}
            initialVisibleMonth={null}
            focusedInput={dateRangePickerFocusedInput}
            onDatesChange={onDatesChange}
            onFocusChange={setDateRangePickerFocusedInput}
            startDate={checkInDate ? moment(checkInDate) : null}
            endDate={checkOutDate ? moment(checkOutDate) : null}
            isDayBlocked={(day: moment.Moment) => isDayBlocked(day)}
            isOutsideRange={(day: moment.Moment) =>
              isStartDayBlocked(day) ||
              (isEndDayBlocked(day) && !isDayBlocked(day))
            }
            hideKeyboardShortcutsPanel={true}
            disabled={availabilityDataLoading}
            minimumNights={minimumNightsForSelectedDate}
            daysViolatingMinNightsCanBeClicked={false}
            getMinNightsForHoverDate={getMinNightsForHoverDate}
          />
        </div>

        <div className="flex w-full justify-end pb-2 pr-2">
          <Button variant="text" onClick={onClearDatesClick}>
            Clear
          </Button>
        </div>
      </Dropdown>
      <Dropdown
        dropdownWidth={'full'}
        reference={guestCountInputRef.current}
        isOpen={guestCountFocusedInput}
        onClickAway={() => {
          setGuestCountFocusedInput(false);
        }}
        data-id="guest-count-picker-dropdown"
        className="absolute p-4 mt-0 space-y-4 divide-y divide-gray-light text-left"
      >
        <div className="flex space-x-6 justify-between">
          <div className="flex flex-col">
            <Typography variant="subtitle2" component="p">
              Adults
            </Typography>
            <Typography variant="body2" component="p">
              Ages 13 or above
            </Typography>
          </div>
          <div className="flex space-x-2 justify-between">
            <ButtonIcon
              icon={IconNames.MINUS}
              ariaDescribedby="remove adult"
              variant="text"
              iconSize="sm"
              color="gray"
              onClick={() =>
                setAdultCount((val) => {
                  if (val > 1) {
                    return val - 1;
                  } else {
                    return val;
                  }
                })
              }
              disabled={!adultCount || adultCount === 1}
            />
            <input
              type="number"
              className="w-6 text-center"
              max={listing.guestCapacity}
              min={1}
              value={adultCount || 1}
              data-testid={'@quotes-form/adult-count'}
              onChange={(e) => {
                handleValueChange(e, setAdultCount, guestCapacity.ADULTS);
              }}
            />
            <ButtonIcon
              icon={IconNames.PLUS}
              ariaDescribedby="add adult"
              variant="text"
              iconSize="sm"
              color="gray"
              onClick={() =>
                setAdultCount((val) => {
                  return val >= guestCapacity.ADULTS
                    ? guestCapacity.ADULTS
                    : val + 1;
                })
              }
              disabled={guestCapacityReached}
            />
          </div>
        </div>
        <div className="flex space-x-6 justify-between pt-4">
          <div className="flex flex-col">
            <Typography variant="subtitle2" component="p">
              Children
            </Typography>
            <Typography variant="body2" component="p">
              Ages 2-12
            </Typography>
          </div>
          <div className="flex space-x-2 justify-between">
            <ButtonIcon
              icon={IconNames.MINUS}
              ariaDescribedby="remove child"
              variant="text"
              iconSize="sm"
              color="gray"
              onClick={() =>
                setChildCount((val) => {
                  if (val > 0) {
                    return val - 1;
                  } else {
                    return val;
                  }
                })
              }
            />
            <input
              type="number"
              className="w-6 text-center"
              value={childCount}
              data-testid={'@quotes-form/child-count'}
              onChange={(e) => {
                handleValueChange(e, setChildCount, guestCapacity.CHILDREN);
              }}
            />
            <ButtonIcon
              icon={IconNames.PLUS}
              ariaDescribedby="add child"
              variant="text"
              iconSize="sm"
              color="gray"
              onClick={() =>
                setChildCount((val) => {
                  return val >= guestCapacity.CHILDREN
                    ? guestCapacity.CHILDREN
                    : val + 1;
                })
              }
              disabled={guestCapacityReached}
            />
          </div>
        </div>

        <div className="flex space-x-6 justify-between pt-4">
          <div className="flex flex-col">
            <Typography variant="subtitle2" component="p">
              Infants
            </Typography>
            <Typography variant="body2" component="p">
              Under 2
            </Typography>
          </div>
          <div className="flex space-x-2 justify-between">
            <ButtonIcon
              icon={IconNames.MINUS}
              ariaDescribedby="remove infant"
              variant="text"
              iconSize="sm"
              color="gray"
              onClick={() =>
                setInfantCount((val) => {
                  if (val > 0) {
                    return val - 1;
                  } else {
                    return val;
                  }
                })
              }
            />
            <input
              type="number"
              className="w-6 text-center"
              value={infantCount}
              data-testid={'@quotes-form/infant-count'}
              onChange={(e) => {
                handleValueChange(e, setInfantCount, guestCapacity.INFANTS);
              }}
            />
            <ButtonIcon
              icon={IconNames.PLUS}
              ariaDescribedby="add infant"
              variant="text"
              iconSize="sm"
              color="gray"
              onClick={() =>
                setInfantCount((val) => {
                  if (val > 5) {
                    return 5;
                  } else {
                    return val + 1;
                  }
                })
              }
            />
          </div>
        </div>

        <div className="flex justify-between pt-4">
          <div className="flex flex-col justify-center">
            <Typography variant="subtitle2" component="p">
              Pets
            </Typography>
          </div>
          <div className="flex space-x-2 justify-between">
            <ButtonIcon
              icon={IconNames.MINUS}
              ariaDescribedby="remove pet"
              variant="text"
              iconSize="sm"
              color="gray"
              onClick={() =>
                setPetCount((val) => {
                  if (val > 0) {
                    return val - 1;
                  } else {
                    return val;
                  }
                })
              }
              disabled={petsNotAllowed}
            />
            <input
              type="number"
              className="w-6 text-center"
              value={petCount}
              data-testid={'@quotes-form/pet-count'}
              onChange={(e) => {
                handleValueChange(e, setPetCount, guestCapacity.PETS);
              }}
              disabled={petsNotAllowed}
            />
            <ButtonIcon
              icon={IconNames.PLUS}
              ariaDescribedby="add pet"
              variant="text"
              iconSize="sm"
              color="gray"
              onClick={() =>
                setPetCount((val) => {
                  if (val > 2) {
                    return 2;
                  } else {
                    return val + 1;
                  }
                })
              }
              disabled={petsNotAllowed}
            />
          </div>
        </div>
      </Dropdown>

      <div className="mt-5 w-full text-white">
        {(shouldShowRatesAndAvailability && !quoteData) ||
        (shouldShowRatesAndAvailability &&
          quoteData?.bookingQuoteByListingAndDates.__typename ==
            'ValidationError') ||
        (shouldShowRatesAndAvailability && !datesPresent) ? (
          <Button
            className="justify-center whitespace-nowrap h-12"
            onClick={() => {
              setDateRangePickerFocusedInput('startDate');
            }}
            isFullWidth={true}
            data-id={cta.analyticsRef}
            isLoading={quoteDataLoading}
          >
            {cta.label}
          </Button>
        ) : (
          cta.bookingUrl && (
            <AnalyticsRef name={cta.analyticsRef}>
              <Link
                isButton={true}
                isFullWidth={true}
                to={cta.bookingUrl}
                target="_blank"
                rel="noreferrer"
                className="h-12 text-primary-main"
                isLoading={quoteDataLoading}
              >
                {cta.label}
              </Link>
              {cta.subtext && (
                <div className="justify-center flex flex-row flex-cols-2 w-full text-xs space-x-1 whitespace-nowrap pt-2">
                  <div className="text-secondary-dark font-light">
                    {cta.subtext}
                  </div>
                  <div className="text-secondary-dark font-medium">
                    {listing.account.companyName}
                  </div>
                </div>
              )}
            </AnalyticsRef>
          )
        )}
      </div>

      <div className="mb-0">
        {datesPresent &&
          quoteData &&
          quoteData.bookingQuoteByListingAndDates &&
          quoteData.bookingQuoteByListingAndDates.__typename ==
            'BookingQuote' &&
          quoteData.bookingQuoteByListingAndDates.quoteItems?.length > 0 &&
          quoteData.bookingQuoteByListingAndDates.totalWithoutTaxesCents && (
            <div className="mt-6 w-full text-primary-main">
              <ListingQuoteBreakdown
                numberOfNights={numberOfNights}
                totalWithoutTaxes={
                  quoteData.bookingQuoteByListingAndDates.totalWithoutTaxesCents
                }
                quoteBreakdownItems={
                  quoteData.bookingQuoteByListingAndDates.quoteItems
                }
              />
            </div>
          )}

        {datesPresent &&
          quoteData &&
          quoteData.bookingQuoteByListingAndDates &&
          quoteData.bookingQuoteByListingAndDates.__typename ==
            'BookingQuote' &&
          quoteData.bookingQuoteByListingAndDates.quoteItems?.length == 0 &&
          quoteData.bookingQuoteByListingAndDates.totalCostCents && (
            <div className="mt-6 w-full text-primary-main">
              <div className="flex w-full justify-between space-x-2 py-2">
                <Typography component="span" variant="subtitle2">
                  Charge Total
                </Typography>

                <Typography component="span" variant="subtitle2">
                  {formatWholeDollarAmount(
                    quoteData.bookingQuoteByListingAndDates.totalCostCents
                  )}
                </Typography>
              </div>
            </div>
          )}

        {datesPresent &&
          quoteData &&
          quoteData.bookingQuoteByListingAndDates &&
          quoteData.bookingQuoteByListingAndDates.__typename ==
            'BookingQuoteUnavailable' && (
            <div className="mt-4 px-2 text-primary-main font-light text-xs lg:text-sm flex flex-col w-full justify-between">
              This property is not available for your selected dates. Please try
              different dates.
            </div>
          )}

        {datesPresent &&
          quoteData &&
          quoteData.bookingQuoteByListingAndDates &&
          quoteData.bookingQuoteByListingAndDates.__typename ==
            'ServiceUnavailableError' && (
            <div className="mt-4 px-2 text-primary-main font-light text-xs lg:text-sm flex flex-col w-full justify-between">
              {quoteData.bookingQuoteByListingAndDates.message}
            </div>
          )}

        {datesPresent &&
          quoteData &&
          quoteData.bookingQuoteByListingAndDates &&
          quoteData.bookingQuoteByListingAndDates.__typename ==
            'ValidationError' && (
            <div className="mt-4 px-2 text-primary-main font-light text-xs lg:text-sm flex flex-col w-full justify-between">
              {quoteData.bookingQuoteByListingAndDates.message}
            </div>
          )}

        {shouldShowRatesAndAvailability && datesPresent ? (
          <div className="mt-4 border-t border-primary-dark">
            <div className="mt-5">
              <QuotesCompare />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
